import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Visibility, VisibilityOff, Person, Lock, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import type { LoginRequest } from '../types/index';
import { apiService } from '../services/api';
import LandingNav from '../components/landing/LandingNav';
import LandingHero from '../components/landing/LandingHero';
import LiveOpsSnapshot from '../components/landing/LiveOpsSnapshot';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';
import WorkflowSection from '../components/landing/WorkflowSection';
import ModulesSection from '../components/landing/ModulesSection';
import IntegrationSection from '../components/landing/IntegrationSection';
import LandingFooter from '../components/landing/LandingFooter';
import {
  getLandingCopy,
  landingPalette,
  type LandingLocale,
  type LandingThemeMode,
} from '../components/landing/landingCopy';

const HomePage: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginRequest>({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<LandingThemeMode>('dark');
  const [locale, setLocale] = useState<LandingLocale>('en');
  const [stats, setStats] = useState({
    totalIncidents: 0,
    openIncidents: 0,
    activeUsers: 0,
    totalLhdForms: 0,
    totalHandovers: 0,
    responseTime: '—',
    slaCompliance: null as number | null,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const copy = useMemo(() => getLandingCopy(locale), [locale]);
  const palette = landingPalette[themeMode];
  const galleryShot = copy.gallery[galleryIndex] ?? copy.gallery[0];
  const stepGallery = (delta: number) => {
    if (copy.gallery.length < 2) return;
    setGalleryIndex((current) => (current + delta + copy.gallery.length) % copy.gallery.length);
  };

  useEffect(() => {
    if (galleryOpen || copy.gallery.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setGalleryIndex((current) => (current + 1) % copy.gallery.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [copy.gallery.length, galleryOpen]);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await apiService.getPublicSummaryStats();
        if (cancelled) return;
        const data = (res.data as any)?.data;
        if (data) {
          const minutes = data.avgResponseMinutes;
          setStats((p) => ({
            ...p,
            totalIncidents: data.totalIncidents ?? 0,
            openIncidents: data.openIncidents ?? 0,
            activeUsers: data.totalUsers ?? 0,
            totalLhdForms: data.totalLhdForms ?? 0,
            totalHandovers: data.totalHandovers ?? 0,
            responseTime: minutes == null ? '—' : `${minutes}m`,
            slaCompliance: data.slaCompliancePct == null ? null : Number(data.slaCompliancePct),
          }));
        }
      } catch {
        if (!cancelled) {
          setStats((p) => ({ ...p, totalIncidents: 0, totalLhdForms: 0, totalHandovers: 0 }));
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    void fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(credentials);
      navigate('/app/dashboard', { replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      const isNetworkError = !err.response && (err.message === 'Network Error' || err.code === 'ERR_NETWORK');
      setError(
        msg ||
          (isNetworkError
            ? 'Cannot connect to server. Ensure both frontend (5173) and backend (3001) are running.'
            : 'Authentication failed. Please verify your credentials.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      lang={locale}
      sx={{
        minHeight: '100vh',
        bgcolor: palette.pageBg,
        color: palette.text,
        fontFamily: '"Cairo", "Outfit", system-ui, sans-serif',
        transition: 'background-color 0.25s ease, color 0.25s ease',
      }}
    >
      <LandingNav
        onSignIn={() => setLoginOpen(true)}
        onNavigate={scrollTo}
        copy={copy.nav}
        palette={palette}
        themeMode={themeMode}
        locale={locale}
        onToggleTheme={() => setThemeMode((m) => (m === 'dark' ? 'light' : 'dark'))}
        onToggleLocale={() => setLocale((l) => (l === 'en' ? 'ar' : 'en'))}
      />

      <LandingHero onSignIn={() => setLoginOpen(true)} copy={copy.hero} palette={palette} />

      <LiveOpsSnapshot
        loading={statsLoading}
        stats={{
          totalIncidents: stats.totalIncidents,
          activeUsers: stats.activeUsers,
          responseTime: stats.responseTime,
          slaCompliance: stats.slaCompliance,
        }}
        copy={copy.stats}
        palette={palette}
      />

      <WorkflowSection copy={copy.workflow} palette={palette} />

      <ModulesSection
        totalLhdForms={stats.totalLhdForms}
        totalHandovers={stats.totalHandovers}
        copy={copy.modules}
        palette={palette}
      />

      <IntegrationSection copy={copy.integration} palette={palette} />

      <Box
        id="about"
        component="section"
        sx={{
          py: { xs: 3, md: 4 },
          bgcolor: palette.pageBg,
          borderTop: `1px solid ${palette.border}`,
          color: palette.text,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontFamily: '"Outfit", "Cairo", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 2,
            }}
          >
            {copy.about.title}
          </Typography>

          {galleryShot && (
            <Box
              onClick={() => setGalleryOpen(true)}
              sx={{
                borderRadius: 2,
                border: `1px solid ${palette.border}`,
                background: palette.cardBg,
                overflow: 'hidden',
                cursor: 'pointer',
                maxWidth: 960,
              }}
            >
              <Box
                component="img"
                src={galleryShot.imageUrl}
                alt={galleryShot.title}
                sx={{
                  width: '100%',
                  maxHeight: 520,
                  objectFit: 'contain',
                  display: 'block',
                  bgcolor: '#0B1016',
                }}
              />
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {galleryShot.title}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.muted }}>
                  {galleryShot.description}
                </Typography>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      <LandingFooter copy={copy.footer} palette={palette} themeMode={themeMode} />

      <Dialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0B1016',
            color: palette.text,
            border: `1px solid ${palette.border}`,
            m: 1,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
          {galleryShot?.title}
          <IconButton
            aria-label="Close screenshot"
            onClick={() => setGalleryOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: palette.text }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ position: 'relative', px: { xs: 5, md: 7 }, pb: 3 }}>
          {galleryShot && (
            <Box
              component="img"
              src={galleryShot.imageUrl}
              alt={galleryShot.title}
              sx={{ width: '100%', maxHeight: '78vh', objectFit: 'contain', display: 'block' }}
            />
          )}
          {copy.gallery.length > 1 && (
            <>
              <IconButton
                aria-label="Previous screenshot"
                onClick={() => stepGallery(-1)}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '45%',
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                aria-label="Next screenshot"
                onClick={() => stepGallery(1)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '45%',
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}
          {galleryShot && (
            <Typography variant="body2" sx={{ color: palette.muted, mt: 1.5 }}>
              {galleryShot.description}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={loginOpen}
        onClose={() => !loading && setLoginOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: themeMode === 'dark' ? '#151B22' : '#fff',
            color: palette.text,
            border: `1px solid ${palette.border}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Outfit", "Cairo", sans-serif', fontWeight: 700, pr: 6 }}>
          {copy.login.title}
          <IconButton
            onClick={() => setLoginOpen(false)}
            disabled={loading}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.muted, mb: 2 }}>
            {copy.login.subtitle}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label={copy.login.username}
                value={credentials.username}
                onChange={(e) => setCredentials((p) => ({ ...p, username: e.target.value }))}
                fullWidth
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={copy.login.password}
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={(e) => setCredentials((p) => ({ ...p, password: e.target.value }))}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: palette.primaryBtn,
                  '&:hover': { bgcolor: palette.primaryBtnHover },
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : copy.login.submit}
              </Button>
              <Button onClick={() => setForgotOpen(true)} sx={{ color: palette.muted, textTransform: 'none' }}>
                Forgot password?
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </Box>
  );
};

export default HomePage;
